import { useCallback, useEffect, useState } from 'react';
import type { HistoryEntry, MeResponse, ProductView } from '@medistock/contracts';
import { ApiError, api } from './api';
import { Login } from './screens/Login';
import { StaffHome } from './screens/StaffHome';
import { UsageSheet } from './screens/UsageSheet';
import { History } from './screens/History';
import { Approvals, Audit, Dashboard, Inventory } from './screens/Manager';
import { Admin } from './screens/Admin';

type Tab = 'record' | 'history' | 'dashboard' | 'inventory' | 'approvals' | 'audit' | 'setup';

const STAFF_TABS: { id: Tab; label: string }[] = [
  { id: 'record', label: 'Record' },
  { id: 'history', label: 'My entries' },
];
const MANAGER_TABS: { id: Tab; label: string }[] = [
  { id: 'dashboard', label: 'Overview' },
  { id: 'inventory', label: 'Stock' },
  { id: 'approvals', label: 'Approvals' },
  { id: 'audit', label: 'Ledger' },
  { id: 'setup', label: 'Setup' },
];

export function App() {
  const [user, setUser] = useState<MeResponse | null>(null);
  const [booted, setBooted] = useState(false);
  const [tab, setTab] = useState<Tab>('record');
  const [products, setProducts] = useState<ProductView[]>([]);
  const [entries, setEntries] = useState<HistoryEntry[]>([]);
  const [picked, setPicked] = useState<ProductView | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [online, setOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine !== false,
  );

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  useEffect(() => {
    api
      .session()
      .then((r) => setUser(r.user))
      .catch(() => setUser(null))
      .finally(() => setBooted(true));
  }, []);

  const reload = useCallback(() => {
    if (!user) return;
    api
      .products()
      .then((r) => setProducts(r.products))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Failed to load products'));
    api
      .history()
      .then((r) => setEntries(r.entries))
      .catch(() => undefined);
  }, [user]);

  useEffect(reload, [reload]);

  const signedIn = (me: MeResponse) => {
    setUser(me);
    setTab(me.role === 'STAFF' ? 'record' : 'dashboard');
  };

  const signOut = async () => {
    await api.logout().catch(() => undefined);
    setUser(null);
    setProducts([]);
    setEntries([]);
  };

  const flash = (message: string) => {
    setNotice(message);
    setError(null);
    reload();
  };

  if (!booted) return <div className="app" />;
  if (!user) return <Login onSignedIn={signedIn} />;

  const tabs = user.role === 'STAFF' ? STAFF_TABS : [...STAFF_TABS, ...MANAGER_TABS];

  return (
    <div className="app">
      <header className="bar">
        <h1>MediStock</h1>
        <div className="who">
          {user.displayName}
          <br />
          {user.locationName}
        </div>
        <button className="ghost" data-testid="sign-out" onClick={() => void signOut()}>
          Sign out
        </button>
      </header>

      <main>
        {!online ? (
          <p className="notice error" data-testid="offline-banner">
            You are offline. Saving is blocked until the connection is back — nothing is queued.
          </p>
        ) : null}
        {notice ? (
          <p className="notice ok" data-testid="notice" onClick={() => setNotice(null)}>
            {notice}
          </p>
        ) : null}
        {error ? (
          <p className="notice error" data-testid="app-error">
            {error}
          </p>
        ) : null}

        {tab === 'record' ? <StaffHome products={products} onPick={setPicked} /> : null}
        {tab === 'history' ? <History entries={entries} onChanged={flash} /> : null}
        {tab === 'dashboard' ? <Dashboard onNotice={flash} /> : null}
        {tab === 'inventory' ? <Inventory products={products} onNotice={flash} reload={reload} /> : null}
        {tab === 'approvals' ? <Approvals onNotice={flash} /> : null}
        {tab === 'audit' ? <Audit /> : null}
        {tab === 'setup' ? (
          <Admin role={user.role} currentUserId={user.id} onNotice={flash} reload={reload} />
        ) : null}
      </main>

      <nav className="tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            aria-current={tab === t.id ? 'page' : undefined}
            onClick={() => {
              setTab(t.id);
              setNotice(null);
            }}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {picked ? (
        <UsageSheet
          product={picked}
          onClose={() => setPicked(null)}
          onSaved={(m) => {
            setPicked(null);
            flash(m);
          }}
        />
      ) : null}
    </div>
  );
}
